/* 2026-02 · Bowker ISBN paste checklist.
 *
 * Bowker and its resellers (ISBN-US.com) don't expose a purchase API,
 * so onboarding a new 10- or 100-pack is a manual portal → paste
 * dance. This checklist walks an admin through the five steps so
 * nothing gets skipped (wrong imprint name, forgotten metadata push,
 * skipping the ONIX export back to Bowker, etc.). Each step
 * remembers its checked state per-admin in localStorage so the
 * checklist reflects real-world progress across sessions.
 *
 * Sits INSIDE the IsbnBankSection card, above the paste textarea.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Circle, ExternalLink, ClipboardList } from 'lucide-react';

const LS_KEY = 'crownnrose_isbn_paste_checklist_v1';

const STEPS = [
  {
    id: 'buy',
    label: 'Buy the block on MyIdentifiers or ISBN-US',
    hint: 'Bowker $295 for a 10-pack · $575 for a 100-pack. ISBN-US $129/single (only cheaper for 1-2 titles).',
    linkLabel: 'Open MyIdentifiers.com',
    linkHref: 'https://www.myidentifiers.com',
  },
  {
    id: 'download',
    label: 'Download the ISBN list from your Bowker account',
    hint: 'MyIdentifiers → Titles → Manage ISBNs → "Download my ISBNs" (CSV or plain-text).',
  },
  {
    id: 'imprint',
    label: 'Verify the imprint name below matches your Bowker record',
    hint: 'The imprint field must match the "Publisher/Imprint" you registered with Bowker. Mismatched imprints break BISG title lookups.',
  },
  {
    id: 'paste',
    label: 'Paste ISBN-13s into the textarea — one per line',
    hint: 'Checksums verified server-side. Duplicates skipped automatically. Rejected rows will be summarised after Save.',
  },
  {
    id: 'onix',
    label: 'After allocating titles, click "Download Bowker ONIX 3.0"',
    hint: 'Uploads the metadata for every allocated ISBN back to Bowker in one XML file. Do this after you\'ve issued a batch of titles, not after every single one.',
  },
];

export default function IsbnPasteChecklist() {
  const [checked, setChecked] = useState({});

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (raw) setChecked(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  const toggle = (id) => {
    setChecked((c) => {
      const next = { ...c, [id]: !c[id] };
      try { window.localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const doneCount = STEPS.filter((s) => checked[s.id]).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);

  return (
    <div className="mt-2 rounded-md border border-primary/30 bg-primary/[0.04] p-3"
         data-testid="isbn-paste-checklist">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <ClipboardList className="h-3.5 w-3.5 text-primary" />
          Bowker paste-in checklist
        </div>
        <div className="flex items-center gap-2 text-[10.5px] text-muted-foreground">
          <span data-testid="isbn-paste-checklist-progress">
            {doneCount} / {STEPS.length} · {pct}%
          </span>
          <div className="h-1 w-16 overflow-hidden rounded-full bg-border">
            <div className="h-1 rounded-full bg-primary transition-all"
                 style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
      <ol className="mt-2 space-y-1.5">
        {STEPS.map((s, i) => {
          const on = !!checked[s.id];
          return (
            <li key={s.id} className="flex items-start gap-2">
              <button type="button" onClick={() => toggle(s.id)}
                      data-testid={`isbn-paste-checklist-step-${s.id}`}
                      aria-pressed={on}
                      aria-label={`Toggle step ${i + 1} — ${s.label}`}
                      className="mt-0.5 shrink-0 rounded-full p-0.5 text-primary hover:bg-primary/10">
                {on
                  ? <CheckCircle2 className="h-4 w-4" />
                  : <Circle className="h-4 w-4 opacity-60" />}
              </button>
              <div className="flex-1">
                <div className={`text-[11.5px] font-semibold ${on ? 'text-muted-foreground line-through' : ''}`}>
                  {i + 1}. {s.label}
                  {s.linkHref && (
                    <a href={s.linkHref} target="_blank" rel="noreferrer"
                       className="ml-1.5 inline-flex items-center gap-0.5 text-[10.5px] font-normal text-primary hover:underline"
                       data-testid={`isbn-paste-checklist-link-${s.id}`}>
                      <ExternalLink className="h-2.5 w-2.5" />
                      {s.linkLabel}
                    </a>
                  )}
                </div>
                <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                  {s.hint}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
